import { getConfig } from './config';
import { store, type AccountView } from './store';

/** Maps widget placeholder id → account view (mirrors main.tsx INLINE_VIEW_PLACEHOLDERS). */
export const PLACEHOLDER_ID_TO_VIEW: Record<string, NonNullable<AccountView>> = {
  'shop-cart-page': 'cart',
  'shop-checkout-page': 'checkout',
  'shop-profile-page': 'account',
  'shop-orders-page': 'orders',
  'shop-tickets-page': 'tickets',
  'shop-subscriptions-page': 'subscriptions',
  'shop-bonuses-page': 'bonus-account',
  'shop-referral-page': 'referral-tree',
  'shop-bonus-transactions-page': 'bonus-transactions',
  'shop-discounts-page': 'discounts',
  'shop-delete-account-page': 'account-delete',
  'shop-login-page': 'checkout-login',
  'shop-signup-page': 'signup',
  'shop-reset-password-page': 'reset-password',
};

/** Pages where contextual login/signup stays on-page (modal), even when Login inline page is enabled. */
const CONTEXTUAL_AUTH_MODAL_PATHS = ['/contactus', '/contact', '/testimonials'];
const INLINE_VIEW_NAMES = new Set<NonNullable<AccountView>>([
  'cart', 'checkout', 'checkout-qnr', 'checkout-confirm', 'checkout-auth',
  'order-success', 'order-return', 'account', 'orders', 'tickets',
  'subscriptions', 'bonus-account', 'bonus-transactions', 'referral-tree',
  'discounts', 'account-delete', 'checkout-login', 'signup', 'reset-password',
]);

/** Apply builder "Inline pages" toggles to store (shop-wide, not only when placeholder DOM exists). */
export function registerConfiguredInlinePlaceholders(): void {
  const configured = getConfig().inlinePlaceholders;
  if (!Array.isArray(configured)) return;
  for (const raw of configured) {
    const id = String(raw ?? '').trim().replace(/^#+/, '');
    if (!id) continue;
    if (INLINE_VIEW_NAMES.has(id as NonNullable<AccountView>)) {
      store.registerInlinePlaceholder(id as NonNullable<AccountView>);
      continue;
    }
    const view = PLACEHOLDER_ID_TO_VIEW[id];
    if (view) store.registerInlinePlaceholder(view);
    if (id === CHECKOUT_INLINE_PLACEHOLDER_ID) {
      CHECKOUT_FLOW_INLINE_VIEWS.forEach((v) => store.registerInlinePlaceholder(v));
    }
  }
}

export function isViewInlinePageEnabled(view: NonNullable<AccountView>): boolean {
  return store.hasInlinePlaceholder(view);
}

/** Whether this exact builder placeholder is enabled (without checkout-flow aliases). */
export function isInlinePlaceholderConfigured(id: string): boolean {
  const configured = getConfig().inlinePlaceholders;
  if (!Array.isArray(configured)) return true;
  const normalizedId = id.replace(/^#+/, '');
  return configured.some((raw) => String(raw ?? '').trim().replace(/^#+/, '') === normalizedId);
}

const VIEW_TO_PLACEHOLDER_ID: Partial<Record<NonNullable<AccountView>, string>> = Object.fromEntries(
  Object.entries(PLACEHOLDER_ID_TO_VIEW).map(([id, view]) => [view, id]),
) as Partial<Record<NonNullable<AccountView>, string>>;

const CHECKOUT_INLINE_PLACEHOLDER_ID = 'shop-checkout-page';
const CHECKOUT_FLOW_INLINE_VIEWS: NonNullable<AccountView>[] = [
  'checkout', 'checkout-qnr', 'checkout-confirm', 'order-success', 'order-return',
  'checkout-auth', 'checkout-login',
];

/** Inline page is configured in builder AND its placeholder exists on the current document. */
export function isInlineViewOnCurrentPage(view: NonNullable<AccountView>): boolean {
  if (typeof document !== 'undefined' && CHECKOUT_FLOW_INLINE_VIEWS.includes(view)) {
    if (document.getElementById(CHECKOUT_INLINE_PLACEHOLDER_ID)) {
      return store.hasInlinePlaceholder(view);
    }
  }
  const id = VIEW_TO_PLACEHOLDER_ID[view];
  if (!id || typeof document === 'undefined') return false;
  return isInlinePlaceholderConfigured(id) && !!document.getElementById(id);
}

export function preferAuthModalOnCurrentPage(): boolean {
  if (typeof window === 'undefined') return false;
  let pathOnly = window.location.pathname.replace(/\/+$/, '') || '/';
  if (window.location.pathname.includes('/preview-proxy')) {
    pathOnly = (new URLSearchParams(window.location.search).get('path') ?? '/').replace(/\/+$/, '') || '/';
  }
  return CONTEXTUAL_AUTH_MODAL_PATHS.some((p) => pathOnly === p || pathOnly.endsWith(p));
}
